import React, { useEffect, useReducer, useState, useContext } from 'react'
import PropTypes from 'prop-types'
import uuidv4 from 'uuid/v4'

import { ErrorBoundary } from 'helpers'

import initializer from './initializer'
import reducer from './reducer'
import { CUSTOMER_SUPPORT_EMAIL } from 'components_v2/common'

const idempotencyKey = uuidv4()

const ALLOWED_FORMS = [
  'user',
  'membership',
  'redemption',
  'payment',
  'customFields'
]

const initialState = {
  formErrors: null,
  idempotencyKey
}

export const Store = React.createContext(null)
export const useSubscribeContext = () => useContext(Store)

const SubscribeProvider = ({ children, forms, ...options }) => {
  if (!Array.isArray(forms)) {
    throw Error('Forms must be provided to Subscribe engine as an array')
  }

  // Ensure that the user isn't requesting a form that we do not provide.
  forms.forEach((form) => {
    if (!ALLOWED_FORMS.includes(form)) {
      throw Error(
        `${form} is not a valid form for the Subscribe engine. Please only include one of the following [${ALLOWED_FORMS.join(
          ', '
        )}]`
      )
    }
  })

  if (options.idempotencyKey) {
    // the user supplied an idempotency key on initialization. Use that instead of the auto-generated one.
    // This is for multi-page forms where a user maintains a single key across multiple pages. (e.g. /subscribe flow)
    initialState.idempotencyKey = options.idempotencyKey
  }

  if (options.offer) {
    // if the user is subscribing through a promotion
    // to differentiate between promotional plans and regular plans
    initialState.isAnOffer = true
  }

  const [state, dispatch] = useReducer(reducer, initialState)

  // formState holds all of the returns from initializer() as well as a loaded boolean.
  // Children elements only load after formState.loaded is set to true.
  const [formState, setFormState] = useState({ loaded: false })

  useEffect(() => {
    // Initializer function sets up the actions, initialValues, and validators that the context children will need to work properly.

    const initialize = async () => {
      const { actions, initialValues, validators, loadError } =
        await initializer({ dispatch, forms, options })

      setFormState({
        ...formState,
        actions,
        initialValues,
        validators,
        loadError,
        loaded: true
      })
    }

    initialize()
  }, [options.country])

  return (
    <Store.Provider value={{ state, ...formState }}>
      {!formState.loadError ? (
        formState.loaded && children
      ) : (
        <p>
          We apologize, but there was an error loading this form. Please try
          again or contact {CUSTOMER_SUPPORT_EMAIL} for additional assistance.
          <br />
          <br />
          <span style={{ color: 'red' }}>{formState.loadError}</span>
        </p>
      )}
    </Store.Provider>
  )
}

SubscribeProvider.propTypes = {
  children: PropTypes.node,
  forms: PropTypes.oneOfType([PropTypes.string, PropTypes.array]).isRequired
}

const SafeSubscribeProvider = (props) => (
  <ErrorBoundary>
    <SubscribeProvider {...props} />
  </ErrorBoundary>
)

export default SafeSubscribeProvider
